/** The tours a tour-specific notification is scoped to.
 *
 *  One tour reads better as its name than as "1 tour", so it links directly.
 *  More than one collapses to "N tours" with the list behind a popover — a
 *  tour-specific notification can carry dozens, and an overview row cannot
 *  spend that much width.
 *
 *  Every link opens in a new tab: the timeline is a place you scan and compare
 *  from, and navigating away loses the tenant, density and filters you set up.
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Link, Popover, Stack, Typography } from '@mui/material';
import { useState, type MouseEvent } from 'react';

interface TourRef {
  id: number;
  name: string;
}

interface Props {
  tours: TourRef[] | undefined;
  /** Absent ⇒ render names as plain text (a proposal has no tours to open). */
  href?: (id: number) => string;
  /** `list` always expands; `summary` collapses >1 behind a popover. */
  variant?: 'summary' | 'list';
}

function TourLink({ tour, href }: { tour: TourRef; href?: (id: number) => string }) {
  if (!href) {
    return (
      <Typography variant="body2" component="span">
        {tour.name}
      </Typography>
    );
  }
  return (
    <Link
      href={href(tour.id)}
      target="_blank"
      rel="noopener"
      variant="body2"
      underline="hover"
      onClick={(e) => e.stopPropagation()}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}
    >
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {tour.name}
      </Box>
      <OpenInNewIcon sx={{ fontSize: 12, flexShrink: 0 }} />
    </Link>
  );
}

export function TourLinks({ tours, href, variant = 'summary' }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!tours?.length) return null;

  if (variant === 'list' || tours.length === 1) {
    return (
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        {tours.map((tour) => (
          <TourLink key={tour.id} tour={tour} href={href} />
        ))}
      </Stack>
    );
  }

  const open = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={open}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {tours.length} tours
      </Link>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // The timeline's inspector Drawer sits at zIndex.modal; a popover
        // opened from inside it has to clear that too (lessons.md).
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
        slotProps={{ paper: { sx: { p: 1, maxWidth: 360, maxHeight: 320 } } }}
      >
        <Stack spacing={0.5}>
          {tours.map((tour) => (
            <TourLink key={tour.id} tour={tour} href={href} />
          ))}
        </Stack>
      </Popover>
    </>
  );
}
